import moment from 'moment';
import { useCallback } from 'react';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { EventSplitParams } from 'components/organisms/resource-management/scheduler-config/listeners';
import { bulkActionsMapActions } from 'components/organisms/resource-management/utils/bulk-actions';
import { shiftDateIfOnNonWorkingDay } from 'components/organisms/resource-management/utils/dates';
import {
  asAllocationPayload,
  idForNewAllocation,
  newAllocationEvent,
  NewAllocationParam,
} from 'components/organisms/resource-management/utils/events-conversion/common';
import { numberOfWorkingDaysBetweenDates } from 'components/organisms/timesheets/common/utils';
import { ExtendedManagerProps } from '../../types';

type ReturnProps = (params: EventSplitParams) => void;

export const useAllocationSplitHandler = (
  props: ExtendedManagerProps,
): ReturnProps => {
  const {
    schedulerRef,
    groupBy,
    workingDaysConfig,
    bulkUpdate,
    excludeNonWorkingDays,
    resourceManagementConfig,
  } = props;

  return useCallback(
    ({ event: allocationEvent, splitAt }: EventSplitParams) => {
      const { createAllocation, updateAllocation, actionsMap } =
        bulkActionsMapActions();
      const startDateForNewAllocation = shiftDateIfOnNonWorkingDay(
        moment(splitAt),
        workingDaysConfig,
      );
      const newEndDateForOldAllocation = shiftDateIfOnNonWorkingDay(
        moment(splitAt).subtract(1, 'day').endOf('day'),
        workingDaysConfig,
        false,
      );

      const newAllocationParams: NewAllocationParam = {
        id: idForNewAllocation(),
        ...(allocationEvent.get('userId') != null
          ? {
              type: ResourceType.User,
              userId: allocationEvent.get('userId'),
              roleId: allocationEvent.get('roleId'),
              referenceTeamId: allocationEvent.get('referenceTeamId'),
              overAllocations: allocationEvent.get('overAllocations'),
            }
          : {
              teamId: allocationEvent.get('teamId'),
              roleId: allocationEvent.get('roleId'),
              type: ResourceType.Team,
            }),
        minutes: allocationEvent.get('minutes'),
        billable: allocationEvent.get('billable'),
        totalMinutes:
          numberOfWorkingDaysBetweenDates(
            startDateForNewAllocation,
            moment(allocationEvent.endDate),
            workingDaysConfig,
          ) * allocationEvent.get('minutes'),
        projectPhase: allocationEvent.get('projectPhase'),
        startDate: startDateForNewAllocation.format(DEFAULT_DATE_FORMAT),
        endDate: moment(allocationEvent.endDate).format(DEFAULT_DATE_FORMAT),
        projectId: allocationEvent.get('projectId'),
        medium: allocationEvent.get('medium'),
      };

      schedulerRef.current.instance.suspendRefresh();
      const tempAllocation = newAllocationEvent(
        groupBy,
        newAllocationParams,
        {
          excludeNonWorkingDays,
          workingDaysConfig,
        },
        resourceManagementConfig?.softAllocations.enabled,
      );
      const [addedEvent] =
        schedulerRef.current.instance.eventStore.add(tempAllocation);

      createAllocation(asAllocationPayload(addedEvent), tempAllocation.id);

      const totalMinutesForOldAllocation =
        numberOfWorkingDaysBetweenDates(
          moment(allocationEvent.startDate),
          moment(newEndDateForOldAllocation),
          workingDaysConfig,
        ) * allocationEvent.get('minutes');

      allocationEvent.setEndDate(newEndDateForOldAllocation.toDate(), false);
      allocationEvent.set('totalMinutes', totalMinutesForOldAllocation);
      allocationEvent.set('splitModeActive', false);
      updateAllocation({
        ...asAllocationPayload(allocationEvent),
        endDate: newEndDateForOldAllocation.format(DEFAULT_DATE_FORMAT),
      });

      bulkUpdate(actionsMap).finally(() => {
        schedulerRef.current.instance.resumeRefresh(true);
      });
    },
    [
      bulkUpdate,
      excludeNonWorkingDays,
      groupBy,
      resourceManagementConfig?.softAllocations.enabled,
      schedulerRef,
      workingDaysConfig,
    ],
  );
};
